import outdent from "outdent";
import { customAuth } from "../standardAuthPatterns";
import { defineConnector } from "../types";
import logo from "../gmail/logo.svg";

const PASSWORD_PLACEHOLDER = "__3B_SMTP_PASSWORD_PLACEHOLDER__";
const HOST = "smtp.gmail.com";
const PORT = "587";
const TLS = "starttls";

function connectionString({ username }: Record<string, string>) {
  const params = new URLSearchParams({ requireTLS: "true" });
  const encodedUsername = encodeURIComponent(username ?? "");
  return `smtp://${encodedUsername}:${PASSWORD_PLACEHOLDER}@${HOST}:${PORT}?${params}`;
}

export const gmailSmtp = defineConnector({
  identifier: "gmailSmtp",
  name: "Gmail (SMTP)",
  tagLine: "Send email through Gmail’s SMTP relay",
  logo,
  tags: ["communication"],
  protocol: "smtp",
  connectionTest: { protocol: "smtp", connectionString },
  defaultUrlPattern: () => "",
  customizeStepHandledInline: true,
  auth: customAuth("smtp"),
  steps: [
    {
      title: "Create a Google App Password",
      items: [
        {
          kind: "markdown",
          content: outdent`
            Gmail’s SMTP server requires an [App Password](https://myaccount.google.com/apppasswords) — your regular Google account password will not work. App Passwords require 2-Step Verification to be enabled on your Google account.

            1. Go to [Google App Passwords](https://myaccount.google.com/apppasswords).
            2. Enter a name (e.g. “3B”) and click **Create**.
            3. Copy the 16-character password Google generates.
          `,
        },
      ],
    },
    {
      title: "Enter your Gmail credentials",
      items: [
        {
          kind: "input",
          label: "Name",
          required: true,
          defaultValue: "Gmail",
          target: { kind: "connectedApp", field: "name" },
        },
        {
          kind: "input",
          label: "Gmail address",
          required: true,
          placeholder: "you@gmail.com",
          target: { kind: "configuration", field: "username" },
        },
        {
          kind: "input",
          label: "App Password",
          required: true,
          helperText: "The 16-character password from Google App Passwords.",
          target: { kind: "secrets", field: "password" },
        },
        {
          kind: "input",
          label: "",
          defaultValue: HOST,
          target: { kind: "configuration", field: "host" },
          visibleWhen: [
            {
              target: { kind: "configuration", field: "host" },
              value: "__never__",
            },
          ],
        },
        {
          kind: "input",
          label: "",
          defaultValue: PORT,
          target: { kind: "configuration", field: "port" },
          visibleWhen: [
            {
              target: { kind: "configuration", field: "port" },
              value: "__never__",
            },
          ],
        },
        {
          kind: "input",
          label: "",
          defaultValue: TLS,
          target: { kind: "configuration", field: "tls" },
          visibleWhen: [
            {
              target: { kind: "configuration", field: "tls" },
              value: "__never__",
            },
          ],
        },
      ],
    },
  ],
  publicEnvVars: ({ username }) => ({
    SMTP_HOST: HOST,
    SMTP_PORT: PORT,
    SMTP_USERNAME: username ?? "",
    SMTP_PASSWORD: PASSWORD_PLACEHOLDER,
    SMTP_TLS: TLS,
  }),
  usageHint: () =>
    "Send mail through Gmail with SMTP_HOST=smtp.gmail.com, SMTP_PORT=587, SMTP_TLS=starttls. SMTP_USERNAME is the Gmail address and SMTP_PASSWORD is the Google App Password.",
});
